import { skipToken } from "@reduxjs/toolkit/query/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useEvaluatePracticeTypeSuggestionQuery } from "store/queries/practiceTypeSuggestion";
import { IPracticeTypeSuggestionEvaluateRequest } from "types/PracticeTypeSuggestion/PracticeTypeSuggestion";

import { buildEligibilityProcedures } from "./eligibilityProcedures";
import {
  IPracticeTypeRunCompletedEventDetail,
  PRACTICE_TYPE_RUN_COMPLETED_EVENT,
  requestPracticeTypeRun,
} from "./practiceTypeRunEvent";

/** A user typing several CPT codes in a row causes one evaluation, not one per code. */
export const PRACTICE_TYPE_RERUN_DEBOUNCE_MS = 800;

/**
 * CLE-2971 — the one page-side trigger of the Service Type switch.
 *
 * The API switches the Service Type inside the eligibility run, from the CPT codes the request
 * carries. Lincoln staff often run eligibility FIRST and type the CPT codes after, so that run had no
 * codes to switch on; only the page knows a code was added. When the order's set of CPT codes changes
 * after a check ran IN THIS PAGE SESSION, this asks the evaluate endpoint (codes, payer, plan name from
 * the last response, the code last run) and, if a rule applies, re-runs that side's check ONCE as a
 * plain run — the API then does the switch and says so on the response.
 *
 * Guards:
 *  - armed only by a run this page session made (`PRACTICE_TYPE_RUN_COMPLETED_EVENT`), never by mount
 *    or by loading a draft / copy / redo, which restore benefits without running anything;
 *  - compared against the codes that run was SENT with, so codes typed while it was in flight still
 *    count once it completes;
 *  - never after a "Keep …" run: the estimator chose that Service Type;
 *  - at most one automatic re-run per target code per page session;
 *  - debounced, and the answer is read from `currentData`, which belongs to the arguments asked —
 *    a stale answer for other codes can never trigger a run;
 *  - a request arriving while a run is in flight is held by `EligibilityButton` and run when that
 *    run settles (latest wins), never dropped.
 */
export function usePracticeTypeAutoRerun({
  accountId,
  isSecondaryInsurance = false,
}: {
  accountId: string;
  isSecondaryInsurance?: boolean;
}) {
  const { watch } = useFormContext();
  const serviceDetails = watch("serviceDetails");
  const benefitDetails = watch(
    isSecondaryInsurance ? "benefitDetails2" : "benefitDetails"
  );
  const payer = watch(isSecondaryInsurance ? "payer2" : "payer");

  const procedureCodes = useMemo(
    () =>
      buildEligibilityProcedures(serviceDetails)
        .map((procedure) => procedure.code)
        .sort(),
    [serviceDetails]
  );
  const codesKey = procedureCodes.join(",");

  // Latest values for the debounced callback and the run listener.
  const latest = useRef({ procedureCodes, codesKey, benefitDetails, payer });
  latest.current = { procedureCodes, codesKey, benefitDetails, payer };

  /** The codes as of the last run (or evaluation) in this session; null = no run yet, so disarmed. */
  const baselineRef = useRef<string | null>(null);
  const lastRunWasKeepRef = useRef(false);
  const firedTargetsRef = useRef(new Set<string>());
  const [args, setArgs] =
    useState<IPracticeTypeSuggestionEvaluateRequest | null>(null);
  // Bumped by every completed run, so the codes are compared again even when they did not change since.
  const [runCount, setRunCount] = useState(0);

  useEffect(() => {
    const onCompleted = (event: Event) => {
      const { detail } =
        event as CustomEvent<IPracticeTypeRunCompletedEventDetail>;
      if (
        !detail ||
        Boolean(detail.isSecondaryInsurance) !== isSecondaryInsurance
      )
        return;
      // The codes the run was SENT with: a code typed while it was in flight still counts as a change.
      baselineRef.current = detail.procedureCodes
        ? [...detail.procedureCodes].sort().join(",")
        : latest.current.codesKey;
      lastRunWasKeepRef.current = detail.keepPracticeType === true;
      setArgs(null);
      setRunCount((count) => count + 1);
    };
    window.addEventListener(PRACTICE_TYPE_RUN_COMPLETED_EVENT, onCompleted);
    return () =>
      window.removeEventListener(
        PRACTICE_TYPE_RUN_COMPLETED_EVENT,
        onCompleted
      );
  }, [isSecondaryInsurance]);

  useEffect(() => {
    if (
      baselineRef.current === null ||
      lastRunWasKeepRef.current ||
      !accountId ||
      codesKey === "" ||
      codesKey === baselineRef.current
    )
      return undefined;
    const timer = setTimeout(() => {
      const { benefitDetails: stored, payer: currentPayer } = latest.current;
      const ranUnder: string | undefined =
        stored?.request?.practiceTypeCode || undefined;
      if (!ranUnder) return;
      baselineRef.current = latest.current.codesKey;
      setArgs({
        accountId,
        payerId: currentPayer?.payerId ?? null,
        planName: stored?.response?.planCoverageSummary?.PlanName ?? null,
        practiceTypeCode: ranUnder,
        procedureCodes: latest.current.procedureCodes,
      });
    }, PRACTICE_TYPE_RERUN_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [codesKey, accountId, runCount]);

  const { currentData } = useEvaluatePracticeTypeSuggestionQuery(
    args ?? skipToken
  );

  useEffect(() => {
    if (!args || !currentData) return;
    setArgs(null);
    const target = currentData.suggestion?.targetPracticeTypeCode;
    if (!target || firedTargetsRef.current.has(target)) return;
    firedTargetsRef.current.add(target);
    requestPracticeTypeRun({ isSecondaryInsurance });
  }, [args, currentData, isSecondaryInsurance]);
}
